import { zodResolver } from '@hookform/resolvers/zod';
import { Edit2, Plus, Radio, Radar, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { PageHeader } from '../components/AppShell';
import { ExportMenu } from '../components/ExportMenu';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../components/ui/toast';
import {
  useChannels,
  useCreateChannel,
  useDeleteChannel,
  useDetectChannelLiveStream,
  useUpdateChannel,
} from '../lib/api/channels';
import { exportChannels } from '../lib/api/export';
import { slugify } from '../lib/slug';
import type { ChannelInput, PublicChannel } from '../lib/types';

const slugRe = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

const channelSchema = z.object({
  slug: z.string().min(1).regex(slugRe, 'lowercase letters, numbers, hyphens'),
  name: z.string().min(1).max(255),
  description: z.string().max(2000).optional().nullable(),
  streamUrl: z
    .string()
    .url('must be a URL')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v ? v : null)),
  streamStatsUrl: z
    .string()
    .url('must be a URL')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v ? v : null)),
  youtubeLiveStreamId: z
    .string()
    .max(64)
    .regex(/^[A-Za-z0-9_-]*$/, '11-char YouTube video id')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v ? v : null)),
  youtubeChannelId: z
    .string()
    .max(64)
    .regex(/^(UC[A-Za-z0-9_-]{20,30})?$/, 'expected UC… channel id')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v ? v : null)),
  youtubeHandle: z
    .string()
    .max(64)
    .regex(/^(@?[A-Za-z0-9._-]{3,32})?$/, 'expected @handle (letters, digits, . _ -)')
    .or(z.literal(''))
    .optional()
    .transform((v) => (v ? v : null)),
  isActive: z.boolean().default(true),
});

type ChannelForm = z.input<typeof channelSchema>;

export default function Channels(): JSX.Element {
  const [editing, setEditing] = useState<PublicChannel | 'new' | null>(null);
  const lastDialogKey = useRef('closed');
  if (editing !== null) lastDialogKey.current = editing === 'new' ? 'new' : editing.slug;
  const dialogKey = lastDialogKey.current;
  const [confirmDelete, setConfirmDelete] = useState<PublicChannel | null>(null);
  const channelsQ = useChannels();
  const create = useCreateChannel();
  const update = useUpdateChannel();
  const del = useDeleteChannel();
  const { showToast } = useToast();

  return (
    <>
      <Topbar title="Channels" subtitle="Broadcast streams and stats endpoints" />
      <PageHeader
        title="Channels"
        actions={
          <div className="flex items-center gap-2">
            <ExportMenu
              options={[
                { format: 'csv', label: 'CSV', run: () => exportChannels('csv') },
                { format: 'json', label: 'JSON', run: () => exportChannels('json') },
              ]}
            />
            <Button onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" /> New channel
            </Button>
          </div>
        }
      />
      <div className="px-6 pb-10">
        {channelsQ.isLoading ? (
          <Spinner label="Loading channels…" />
        ) : Array.isArray(channelsQ.data?.items) && channelsQ.data.items.length > 0 ? (
          <div className="overflow-hidden rounded-lg border border-line bg-surface shadow-card">
            <table className="table-base">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Stream</th>
                  <th className="w-32">Status</th>
                  <th className="w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {channelsQ.data.items.map((ch) => (
                  <tr key={ch.id}>
                    <td>
                      <div className="flex items-start gap-2">
                        <Radio className="mt-0.5 h-4 w-4 text-brand" />
                        <div>
                          <p className="font-medium text-ink">{ch.name}</p>
                          <p className="font-mono text-xs text-ink-subtle">{ch.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td className="font-mono text-xs">
                      {ch.streamUrl ? (
                        <a
                          href={ch.streamUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-brand hover:underline"
                        >
                          {truncateMiddle(ch.streamUrl, 50)}
                        </a>
                      ) : (
                        <span className="text-ink-subtle">—</span>
                      )}
                      {ch.streamStatsUrl ? (
                        <p className="text-ink-subtle">
                          stats: {truncateMiddle(ch.streamStatsUrl, 50)}
                        </p>
                      ) : null}
                    </td>
                    <td>
                      <Badge variant={ch.isActive ? 'success' : 'neutral'}>
                        {ch.isActive ? 'Active' : 'Disabled'}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setEditing(ch)}
                        aria-label="Edit"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setConfirmDelete(ch)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-danger" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty
            icon={Radio}
            title="No channels yet"
            description="Add a broadcast channel — for example, the main FM/HF stream or a YouTube simulcast."
            action={<Button onClick={() => setEditing('new')}>Add channel</Button>}
          />
        )}
      </div>

      <ChannelDialog
        // Remount per record: the dialog stays mounted between openings, so
        // without this a half-typed form would follow the user to the next
        // channel (its fields are dirty, so `keepDirtyValues` keeps them).
        // Keyed on the last record opened, not on `editing`, so closing
        // doesn't unmount mid-animation.
        key={dialogKey}
        open={editing !== null}
        existing={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSubmit={async (input) => {
          try {
            if (editing && editing !== 'new') {
              await update.mutateAsync({ id: editing.id, input });
              showToast({ title: 'Channel updated', variant: 'success' });
            } else {
              await create.mutateAsync(input);
              showToast({ title: 'Channel created', variant: 'success' });
            }
            setEditing(null);
          } catch (err) {
            showToast({
              title: 'Save failed',
              description: (err as Error).message,
              variant: 'danger',
            });
          }
        }}
        busy={create.isPending || update.isPending}
      />

      <Dialog open={confirmDelete !== null} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete channel?</DialogTitle>
            <DialogDescription>
              This permanently removes <span className="font-mono">{confirmDelete?.slug}</span>.
              Programmes with slots on this channel will block deletion.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={del.isPending}
              onClick={async () => {
                if (!confirmDelete) return;
                try {
                  await del.mutateAsync(confirmDelete.id);
                  showToast({ title: 'Channel deleted', variant: 'success' });
                  setConfirmDelete(null);
                } catch (err) {
                  showToast({
                    title: 'Delete failed',
                    description: (err as Error).message,
                    variant: 'danger',
                  });
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface ChannelDialogProps {
  open: boolean;
  existing: PublicChannel | null;
  onClose: () => void;
  onSubmit: (input: ChannelInput) => void | Promise<void>;
  busy: boolean;
}

function ChannelDialog({
  open,
  existing,
  onClose,
  onSubmit,
  busy,
}: ChannelDialogProps): JSX.Element {
  const detect = useDetectChannelLiveStream();
  const { showToast } = useToast();
  const form = useForm<ChannelForm>({
    resolver: zodResolver(channelSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: existing
      ? {
          slug: existing.slug,
          name: existing.name,
          description: existing.description ?? '',
          streamUrl: existing.streamUrl ?? '',
          streamStatsUrl: existing.streamStatsUrl ?? '',
          youtubeLiveStreamId: existing.youtubeLiveStreamId ?? '',
          youtubeChannelId: existing.youtubeChannelId ?? '',
          youtubeHandle: existing.youtubeHandle ?? '',
          isActive: existing.isActive,
        }
      : {
          slug: '',
          name: '',
          description: '',
          streamUrl: '',
          streamStatsUrl: '',
          youtubeLiveStreamId: '',
          youtubeChannelId: '',
          youtubeHandle: '',
          isActive: true,
        },
  });

  // Auto-derive slug from name while creating, until the user touches the slug field.
  // In edit mode the slug is locked anyway, so don't bother.
  const watchedName = form.watch('name');
  const slugTouched = form.formState.dirtyFields.slug;
  useEffect(() => {
    if (existing) return;
    if (slugTouched) return;
    const candidate = slugify(watchedName ?? '');
    if (candidate) form.setValue('slug', candidate, { shouldDirty: false });
  }, [watchedName, existing, slugTouched, form]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>{existing ? 'Edit channel' : 'New channel'}</DialogTitle>
          <DialogDescription>
            Channels carry one or more programme slots. Slug must be URL-safe.
          </DialogDescription>
        </DialogHeader>
        <form
          // `values` here is already the parsed + transformed schema
          // output — `zodResolver` runs the schema (transforms included)
          // during validation, and react-hook-form only invokes this
          // callback on success with those transformed values. Re-parsing
          // was a leftover that double-ran the transform: fields like
          // `youtubeChannelId` transform `''` → `null` on the first pass,
          // then the second `channelSchema.parse` rejected `null` as
          // input. Cast because `useForm<ChannelForm>` is typed with the
          // INPUT shape but at runtime the values are the OUTPUT shape.
          onSubmit={form.handleSubmit((values) => {
            void onSubmit(values as ChannelInput);
          })}
          // `flex flex-col min-h-0` on the form itself lets its DialogBody
          // child claim the leftover vertical space between header and
          // footer; `min-h-0` is what lets the flex child actually shrink
          // below its intrinsic content height so `overflow-y-auto` inside
          // DialogBody can kick in.
          className="flex min-h-0 flex-1 flex-col gap-4"
        >
          <DialogBody className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ch-name">Display name</Label>
              <Input id="ch-name" {...form.register('name')} placeholder="VON FM" autoFocus />
              {form.formState.errors.name ? (
                <p className="mt-1 text-xs text-danger">{form.formState.errors.name.message}</p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="ch-slug">Slug</Label>
              <Input
                id="ch-slug"
                {...form.register('slug')}
                disabled={!!existing}
                placeholder="auto-from-name"
              />
              {!existing && !slugTouched ? (
                <p className="mt-1 text-xs text-ink-subtle">
                  Auto-generated from name — edit to customise.
                </p>
              ) : null}
              {form.formState.errors.slug ? (
                <p className="mt-1 text-xs text-danger">{form.formState.errors.slug.message}</p>
              ) : null}
            </div>
          </div>

          <div>
            <Label htmlFor="ch-desc">Description</Label>
            <Textarea id="ch-desc" rows={3} {...form.register('description')} />
          </div>

          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label htmlFor="ch-stream">Stream URL</Label>
              <Input
                id="ch-stream"
                {...form.register('streamUrl')}
                placeholder="https://stream.example/von"
              />
              {form.formState.errors.streamUrl ? (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.streamUrl.message}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="ch-yt-channel">YouTube channel</Label>
              {/* Two inputs, either one is sufficient. The Detect button
               *  below uses `channelId` when set, otherwise resolves the
               *  `@handle` to a channel id (one cheap `channels.list` call,
               *  cached 24 h). We accept both so an editor who only has the
               *  handle handy doesn't have to look up the UC… id. */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Input
                    id="ch-yt-channel"
                    {...form.register('youtubeChannelId')}
                    placeholder="UCxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  {form.formState.errors.youtubeChannelId ? (
                    <p className="mt-1 text-xs text-danger">
                      {form.formState.errors.youtubeChannelId.message}
                    </p>
                  ) : null}
                </div>
                <div>
                  <Input
                    id="ch-yt-handle"
                    {...form.register('youtubeHandle')}
                    placeholder="@voiceofnigeriavon"
                  />
                  {form.formState.errors.youtubeHandle ? (
                    <p className="mt-1 text-xs text-danger">
                      {form.formState.errors.youtubeHandle.message}
                    </p>
                  ) : null}
                </div>
              </div>
              <p className="mt-1 text-xs text-ink-subtle">
                UC… channel id or @handle for this channel's YouTube
                presence. Either is enough. Save the form first, then use
                the Detect button below to auto-pin the currently-airing
                live-stream id.
              </p>
            </div>
            <div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label htmlFor="ch-yt-live">YouTube live stream ID</Label>
                  <Input
                    id="ch-yt-live"
                    {...form.register('youtubeLiveStreamId')}
                    placeholder="dQw4w9WgXcQ"
                  />
                </div>
                {/* Detect only works on a saved channel row (the endpoint
                 *  needs an id/slug to persist the discovered video id) and
                 *  only when at least one of channelId/handle is stored. In
                 *  create mode ("new") both conditions fail — the button is
                 *  hidden entirely rather than shown-and-disabled, keeping
                 *  the create form uncluttered. */}
                {existing && (existing.youtubeChannelId || existing.youtubeHandle) ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={detect.isPending}
                    onClick={() => {
                      detect.mutate(existing.id, {
                        onSuccess: (res) => {
                          if (res.detected) {
                            form.setValue('youtubeLiveStreamId', res.videoId, {
                              shouldDirty: true,
                            });
                            showToast({
                              title: 'Live stream detected',
                              description: res.title
                                ? `Pinned: ${res.title}`
                                : `Pinned video id ${res.videoId}`,
                            });
                          } else {
                            showToast({
                              title: 'Nothing currently live',
                              description:
                                'This YouTube channel has no live broadcast right now. Try again when they go live.',
                              variant: 'info',
                            });
                          }
                        },
                        onError: (err) => {
                          showToast({
                            title: 'Detect failed',
                            description:
                              (err as { message?: string })?.message ??
                              'Could not query YouTube — check the API key and quota.',
                            variant: 'danger',
                          });
                        },
                      });
                    }}
                    title="Query YouTube for the channel's currently-airing live broadcast and auto-pin its video id. Costs 100 quota units per click."
                  >
                    <Radar className="h-4 w-4" />
                    {detect.isPending ? 'Detecting…' : 'Detect live'}
                  </Button>
                ) : null}
              </div>
              <p className="mt-1 text-xs text-ink-subtle">
                Pin this channel's currently-live YouTube broadcast — the
                11-char video id. The mobile Watch tab's Live section polls
                this every 30 s. Each new "Go live" event on YouTube mints a
                fresh video id, so re-run Detect (or clear this field) when
                the current broadcast ends.
              </p>
              {form.formState.errors.youtubeLiveStreamId ? (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.youtubeLiveStreamId.message}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="ch-stats">Shoutcast/Icecast stats URL</Label>
              <Input
                id="ch-stats"
                {...form.register('streamStatsUrl')}
                placeholder="https://stream.example/status-json.xsl"
              />
              {form.formState.errors.streamStatsUrl ? (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.streamStatsUrl.message}
                </p>
              ) : null}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2">
            <Label htmlFor="ch-active" className="cursor-pointer">
              Channel is active (visible publicly)
            </Label>
            <Switch
              id="ch-active"
              checked={form.watch('isActive')}
              onCheckedChange={(v) => form.setValue('isActive', v, { shouldDirty: true })}
            />
          </div>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : existing ? 'Save changes' : 'Create channel'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function truncateMiddle(s: string, max: number): string {
  if (s.length <= max) return s;
  const half = Math.floor((max - 1) / 2);
  return `${s.slice(0, half)}…${s.slice(-half)}`;
}
